/**
 * FaceScreen (S4) — face + liveness capture, then the actual punch.
 * Props: { ctx, api, tg, nav, state, setState, params:{direction,branch_id?,geo} }.
 *
 * ORDER MATTERS:
 *   1. api.challenge() RIGHT BEFORE capture; setState({cue}) and DISPLAY the cue
 *      via i18n.cueText(cue). The server ignores any client-chosen cue.
 *   2. Capture a still via <input type="file" accept="image/*" capture="user">
 *      (getUserMedia is BROKEN in the webview). Read → base64, strip prefix.
 *   3. api[direction==='in'?'checkin':'checkout']({lat,lng,accuracy,branch_id,
 *      image_b64}) using params.geo.
 *   4. on ok: setState({result}) then nav.replace(RESULT, {...}); haptic success.
 *   5. on error: haptic error; ERROR_UI. LIVENESS_FAIL/FACE_MISMATCH bump
 *      state.faceFails, ≥3 → force recover 'manager'. CUE_* → restart step 1.
 *      Location-class errors → recover back to S3 (pop).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Screen from '../components/Screen.jsx';
import Button from '../components/Button.jsx';
import Spinner from '../components/Spinner.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { T, cueText, cueArrow } from '../lib/i18n.js';
import { SCREENS } from '../App.jsx';

// Errors that mean "your GPS/branch was wrong" — recover by re-doing LOCATION.
const LOCATION_ERRORS = new Set([
  'OUT_OF_RANGE', 'NO_LOCATION', 'ACCURACY_MISSING', 'ACCURACY_TOO_LOW',
  'BAD_RANGE', 'NO_BRANCH', 'BRANCH_NOT_ALLOWED', 'BRANCH_NO_COORDS',
]);
// Errors that mean "the challenge went stale" — restart from step 1.
const CUE_ERRORS = new Set(['CUE_MISSING', 'CUE_EXPIRED']);
// Face-match / liveness fails that escalate to a manager after 3 tries.
const FACE_FAILS = new Set(['FACE_MISMATCH', 'LIVENESS_FAIL']);

function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = () => reject(new Error('read'));
    r.readAsDataURL(file);
  });
}

export default function FaceScreen({ ctx, api, tg, nav, state, setState, params }) {
  const inputRef = useRef(null);
  const [phase, setPhase] = useState('loading'); // loading | ready | sending | error
  const [cue, setCue] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errCode, setErrCode] = useState(null);
  const [forcedRecover, setForcedRecover] = useState(null);
  // Countdown UX: the server TTL is the real gate (a capture after expiry
  // returns CUE_EXPIRED, which the handler restarts). This countdown just warns
  // the worker and, at 0, marks the cue stale so we offer a one-tap re-challenge
  // instead of letting them capture into a guaranteed CUE_EXPIRED.
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [stale, setStale] = useState(false);
  const [chalNonce, setChalNonce] = useState(0); // bump to restart the timer

  const direction = params?.direction === 'out' ? 'out' : 'in';
  const geo = params?.geo || state.geo || {};
  const branchId = params?.branch_id ?? state.branchId ?? undefined;
  const branchName =
    ctx.branches.find((b) => b.id === branchId)?.name ||
    ctx.branches[0]?.name ||
    '';

  // Step 1 — fetch a fresh challenge cue.
  const loadChallenge = useCallback(async () => {
    setPhase('loading');
    setErrCode(null);
    setForcedRecover(null);
    setPreview(null);
    setStale(false);
    const res = await api.challenge();
    if (res && res.ok) {
      setCue(res.cue);
      setState({ cue: res.cue });
      // Start the urgency countdown from the server-issued ttl (fallback 60).
      const ttl = Number(res.ttl) > 0 ? Math.floor(Number(res.ttl)) : 60;
      setSecondsLeft(ttl);
      setStale(false);
      setChalNonce((n) => n + 1); // restart the timer effect
      setPhase('ready');
    } else {
      tg.haptic('error');
      setErrCode((res && res.error) || 'SERVER_ERROR');
      setPhase('error');
    }
  }, [api, tg, setState]);

  useEffect(() => {
    loadChallenge();
  }, [loadChallenge]);

  // Tick the countdown once per second while a cue is live. At 0, mark stale so
  // the UI stops offering capture and shows a one-tap re-challenge.
  useEffect(() => {
    if (phase !== 'ready' || stale) return undefined;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setStale(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [phase, stale, chalNonce]);

  function pick() {
    if (inputRef.current) inputRef.current.click();
  }

  // Steps 2–4 — capture the still and punch.
  async function onFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setPhase('sending');
    tg.MainButton.showProgress();

    let image_b64;
    try {
      image_b64 = await fileToB64(file);
    } catch (_e) {
      tg.MainButton.hideProgress();
      handleError('MISSING_IMAGE');
      return;
    }

    const payload = {
      lat: geo.lat,
      lng: geo.lng,
      accuracy: geo.accuracy,
      branch_id: branchId,
      image_b64,
    };
    const res = direction === 'in'
      ? await api.checkin(payload)
      : await api.checkout(payload);
    tg.MainButton.hideProgress();

    if (res && res.ok) {
      tg.haptic('success');
      const time = direction === 'in' ? res.check_in_time : res.check_out_time;
      const result = { direction, time, branch: branchName };
      setState({ result });
      nav.replace(SCREENS.RESULT, result);
    } else {
      handleError((res && res.error) || 'SERVER_ERROR');
    }
  }

  function handleError(code) {
    tg.haptic('error');
    if (FACE_FAILS.has(code)) {
      const fails = (state.faceFails || 0) + 1;
      setState({ faceFails: fails });
      if (fails >= 3) setForcedRecover('manager');
    }
    setErrCode(code);
    setPhase('error');
  }

  // MainButton = "Rasmga olish" while a LIVE (non-stale) cue is displayed.
  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    if (phase !== 'ready' || stale) return undefined;
    return tg.MainButton.onClick(pick);
  });

  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    if (phase !== 'ready' || stale) {
      tg.MainButton.hide();
      return undefined;
    }
    tg.MainButton.setText(T.btn_capture);
    tg.MainButton.show();
    return () => tg.MainButton.hide();
  }, [tg, phase, stale]);

  const hiddenInput = (
    <input
      ref={inputRef}
      className="capture-input"
      type="file"
      accept="image/*"
      capture="user"
      onChange={onFile}
    />
  );

  if (phase === 'error') {
    return (
      <Screen title={T.title_face}>
        {hiddenInput}
        <ErrorState
          code={errCode}
          recover={forcedRecover || undefined}
          onRecover={(verb) => {
            if (verb === 'manager') {
              nav.reboot();
            } else if (verb === 'home') {
              nav.reboot();
            } else if (LOCATION_ERRORS.has(errCode)) {
              nav.pop(); // back to S3 LOCATION to re-fix
            } else {
              loadChallenge(); // CUE_* + generic retry → fresh cue + re-capture
            }
          }}
        />
      </Screen>
    );
  }

  return (
    <Screen
      title={T.title_face}
      footer={
        !tg.isTelegram && phase === 'ready' && !stale ? (
          <Button onClick={pick}>{T.btn_capture}</Button>
        ) : null
      }
    >
      {hiddenInput}
      <div className="facewrap">
        <div className="ring">
          <div className="inner">
            {preview ? <img src={preview} alt="" /> : '🙂'}
            {phase !== 'error' ? <div className="scan" /> : null}
          </div>
        </div>

        {phase === 'loading' ? (
          <Spinner size="md" label={T.loading} />
        ) : phase === 'sending' ? (
          <Spinner size="md" label={T.loading} />
        ) : stale ? (
          <div className="cue-stale">
            <div className="cue-head">{T.cue_stale_h}</div>
            <div className="lil">{T.cue_stale_b}</div>
            <Button onClick={loadChallenge}>{T.btn_new_cue}</Button>
          </div>
        ) : (
          <div className="cue-live">
            <div className="cue-arrow" aria-hidden="true">{cueArrow(cue)}</div>
            <div className="cue cue-big">{cueText(cue)}</div>
            <div className="cue-countdown">
              {T.cue_expires_in}: {secondsLeft}s
            </div>
          </div>
        )}

        <div className="lil">{T.face_privacy}</div>
      </div>
    </Screen>
  );
}
